/**
 * Durable unread + mention hydration and read-cursor persistence for Spaces.
 *
 * The server stores mentions once as lightweight markers on each message
 * (fan-out on read), so unread counts and mention flags are reconciled at read
 * time from the member's per-channel read cursor. This hook:
 *
 * - Hydrates `unreadByChannel` / `unreadBySpace` from `GET /spaces/:id/unread`
 *   on login, whenever the member's set of Spaces changes, and on socket
 *   reconnect (server is the source of truth; live socket increments are a
 *   best-effort overlay that this reconciles).
 * - Persists the read cursor via `POST /spaces/:id/channels/:channelId/read`
 *   when the member views a channel and as new messages arrive while viewing,
 *   so returning/offline members never re-see already-read messages.
 * - Re-hydrates after Space notification settings are saved, since the
 *   resolved level decides which channels report counts and mention flags.
 * - While a channel is focused/active, hydrate treats it as read so a stale
 *   GET /unread cannot resurrect the Space badge the member just cleared.
 *
 * @module hooks/spaces/useSpaceUnreadSync
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  ApiResponse,
  ChatConnectionState,
  MarkSpaceChannelReadResult,
  PublicSpace,
  SpaceUnreadSummary,
} from '@adieuu/shared';
import { onSpaceNotificationSettingsSaved } from '../../services/spaceNotificationSettingsEvents';
import type { SpaceChannelUnreadState } from '../../services/spaceSocketHandlers';
import { mapPool } from '../../utils/mapPool';

/** Max concurrent GET /unread requests during a syncAll fan-out. */
const SYNC_CONCURRENCY = 3;

/** Debounce window for reconnect-triggered syncs (reconnect flapping). */
const RECONNECT_SYNC_DEBOUNCE_MS = 300;

/** Settings saves arrive in bursts (toggle clicks); one re-fetch covers the burst. */
export const SETTINGS_SYNC_DEBOUNCE_MS = 500;

type UnreadApiLike = {
  getUnread: (spaceId: string) => Promise<ApiResponse<SpaceUnreadSummary>>;
  markChannelRead: (
    spaceId: string,
    channelId: string,
    params: { lastReadMessageId: string },
  ) => Promise<ApiResponse<MarkSpaceChannelReadResult>>;
};

export interface SpaceUnreadSyncParams {
  api: { spaces: UnreadApiLike };
  isLoggedIn: boolean;
  /** Member's Spaces (drives which summaries to hydrate). */
  spaces: PublicSpace[];
  onStateChange: (handler: (state: ChatConnectionState) => void) => () => void;
  activeSpaceId: string | null;
  activeChannelId: string | null;
  /** Newest message id currently loaded in the active channel (the read target). */
  activeChannelNewestMessageId: string | undefined;
  /**
   * Whether the document/window currently has focus. Read-cursor persistence
   * matches spaceSocketHandlers' viewing gate and is skipped while backgrounded.
   */
  hasFocus: boolean;
  setUnreadByChannel: React.Dispatch<React.SetStateAction<Record<string, SpaceChannelUnreadState>>>;
  setUnreadBySpace: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  /**
   * Fired when the read cursor is posted for the focused channel, including
   * the message id the cursor advanced to. Used to clear matching inbox rows.
   */
  onChannelRead?: (channelId: string, uptoMessageId: string) => void;
  /**
   * Fired after the channel read cursor is persisted. Used to reconcile the
   * inbox badge with the server, including rows beyond the loaded page.
   */
  onChannelReadPersisted?: () => void;
}

/** Sum unread counts for one Space from the channel map. */
function sumSpaceUnread(
  byChannel: Record<string, SpaceChannelUnreadState>,
  spaceId: string,
): number {
  let total = 0;
  for (const state of Object.values(byChannel)) {
    if (state.spaceId === spaceId) total += state.unread;
  }
  return total;
}

function setSpaceAggregate(
  setUnreadBySpace: SpaceUnreadSyncParams['setUnreadBySpace'],
  spaceId: string,
  total: number,
): void {
  setUnreadBySpace((prev) => {
    if (total <= 0) {
      if (!prev[spaceId]) return prev;
      const { [spaceId]: _drop, ...rest } = prev;
      return rest;
    }
    if (prev[spaceId] === total) return prev;
    return { ...prev, [spaceId]: total };
  });
}

export interface SpaceUnreadSyncResult {
  resync: (spaceId?: string | null) => Promise<void>;
}

export function useSpaceUnreadSync(params: SpaceUnreadSyncParams): SpaceUnreadSyncResult {
  const {
    api,
    isLoggedIn,
    spaces,
    onStateChange,
    activeSpaceId,
    activeChannelId,
    activeChannelNewestMessageId,
    hasFocus,
    setUnreadByChannel,
    setUnreadBySpace,
    onChannelRead,
    onChannelReadPersisted,
  } = params;

  const spacesRef = useRef<PublicSpace[]>(spaces);
  spacesRef.current = spaces;

  // Viewing gate for hydrate: a focused active channel is treated as already
  // read so a stale GET /unread cannot resurrect badges the member just cleared.
  const viewingRef = useRef<{
    spaceId: string | null;
    channelId: string | null;
    hasFocus: boolean;
  }>({ spaceId: null, channelId: null, hasFocus: false });
  viewingRef.current = {
    spaceId: activeSpaceId,
    channelId: activeChannelId,
    hasFocus,
  };

  // Stable key over the member's Space ids so hydration re-runs on join/leave
  // but not on unrelated Space object churn (e.g. memberCount updates).
  const spaceIdsKey = useMemo(
    () => spaces.map((s) => s.id).sort().join(','),
    [spaces],
  );

  /**
   * Drop a channel badge and recompute its Space aggregate from what remains.
   * No-ops when the channel entry is already absent so we don't zero the Space
   * badge while hydrate is still in flight (other channels may not be seeded yet).
   */
  const clearViewedChannelLocally = useCallback(
    (spaceId: string, channelId: string) => {
      setUnreadByChannel((prev) => {
        if (!prev[channelId]) return prev;
        const { [channelId]: _, ...rest } = prev;
        setSpaceAggregate(setUnreadBySpace, spaceId, sumSpaceUnread(rest, spaceId));
        return rest;
      });
    },
    [setUnreadByChannel, setUnreadBySpace],
  );

  const applySummary = useCallback(
    (summary: SpaceUnreadSummary) => {
      const viewing = viewingRef.current;
      const suppressChannelId =
        viewing.hasFocus &&
        viewing.spaceId === summary.spaceId &&
        viewing.channelId
          ? viewing.channelId
          : null;

      const channels = summary.channels.map((ch) =>
        ch.channelId === suppressChannelId
          ? { ...ch, unreadCount: 0, mentioned: false }
          : ch,
      );

      setUnreadByChannel((prev) => {
        const next: Record<string, SpaceChannelUnreadState> = {};
        // Drop stale entries for this Space, then re-seed from the server.
        for (const [channelId, state] of Object.entries(prev)) {
          if (state.spaceId !== summary.spaceId) next[channelId] = state;
        }
        for (const ch of channels) {
          if (ch.unreadCount <= 0 && !ch.mentioned) continue;
          next[ch.channelId] = {
            unread: ch.unreadCount,
            mention: ch.mentioned,
            spaceId: summary.spaceId,
          };
        }
        return next;
      });
      setSpaceAggregate(
        setUnreadBySpace,
        summary.spaceId,
        channels.reduce((sum, c) => sum + c.unreadCount, 0),
      );
    },
    [setUnreadByChannel, setUnreadBySpace],
  );

  const syncSpaces = useCallback(
    async (list: readonly PublicSpace[]) => {
      await mapPool(list, SYNC_CONCURRENCY, async (space) => {
        try {
          const res = await api.spaces.getUnread(space.id);
          if (res.success && res.data) applySummary(res.data);
        } catch {
          // Best-effort: a failed hydrate falls back to the live socket overlay
          // and retries on the next reconnect.
        }
      });
    },
    [api, applySummary],
  );

  const syncAll = useCallback(() => syncSpaces(spacesRef.current), [syncSpaces]);

  /** Re-fetch one Space's summary (or every Space when omitted). */
  const resync = useCallback(
    (spaceId?: string | null) => {
      if (!spaceId) return syncAll();
      return syncSpaces(spacesRef.current.filter((s) => s.id === spaceId));
    },
    [syncAll, syncSpaces],
  );

  // Hydrate on login and whenever the member's Space set changes.
  useEffect(() => {
    if (!isLoggedIn || !spaceIdsKey) return;
    void syncAll();
  }, [isLoggedIn, spaceIdsKey, syncAll]);

  // Notification levels shape server counts and mention flags; re-fetch once a
  // change is saved (a defaults change affects every Space).
  useEffect(() => {
    if (!isLoggedIn) return;
    let all = false;
    const pending = new Set<string>();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsub = onSpaceNotificationSettingsSaved((spaceId) => {
      if (spaceId) pending.add(spaceId);
      else all = true;
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = undefined;
        if (all) {
          void syncAll();
        } else {
          void syncSpaces(spacesRef.current.filter((s) => pending.has(s.id)));
        }
        all = false;
        pending.clear();
      }, SETTINGS_SYNC_DEBOUNCE_MS);
    });
    return () => {
      unsub();
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [isLoggedIn, syncAll, syncSpaces]);

  // Re-hydrate on socket reconnect (parity with message/space refresh), since
  // events missed while disconnected would otherwise leave badges stale.
  useEffect(() => {
    if (!isLoggedIn) return;
    // onStateChange fires immediately with the current state; the initial
    // hydrate is handled by the effect above, so treat the current connection as
    // already accounted for and only react to a fresh disconnect → connect.
    let connectedHandled = true;
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;
    const unsub = onStateChange((state) => {
      if (state === 'disconnected' || state === 'reconnecting') {
        connectedHandled = false;
        return;
      }
      if (state !== 'connected' || connectedHandled) return;
      connectedHandled = true;
      if (debounceTimer !== undefined) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = undefined;
        void syncAll();
      }, RECONNECT_SYNC_DEBOUNCE_MS);
    });
    return () => {
      unsub();
      if (debounceTimer !== undefined) clearTimeout(debounceTimer);
    };
  }, [isLoggedIn, onStateChange, syncAll]);

  // Persist the read cursor while viewing a channel. Advances as new messages
  // arrive; a per-channel guard prevents redundant writes for the same target.
  // Matches spaceSocketHandlers: only advance while the window has focus.
  const lastPostedRef = useRef<Record<string, string>>({});
  useEffect(() => {
    if (!isLoggedIn) return;
    if (!hasFocus) return;
    if (!activeSpaceId || !activeChannelId || !activeChannelNewestMessageId) return;
    if (lastPostedRef.current[activeChannelId] === activeChannelNewestMessageId) return;

    const spaceId = activeSpaceId;
    const channelId = activeChannelId;
    const messageId = activeChannelNewestMessageId;
    lastPostedRef.current[channelId] = messageId;

    // Optimistic: keep Space/channel badges and inbox rows clear even if a
    // stale hydrate races in before the POST ack.
    clearViewedChannelLocally(spaceId, channelId);
    onChannelRead?.(channelId, messageId);

    void api.spaces
      .markChannelRead(spaceId, channelId, { lastReadMessageId: messageId })
      .then((res) => {
        if (!res.success) {
          // Allow a retry on the next change if the write failed.
          if (lastPostedRef.current[channelId] === messageId) {
            delete lastPostedRef.current[channelId];
          }
          return;
        }
        clearViewedChannelLocally(spaceId, channelId);
        onChannelReadPersisted?.();
      })
      .catch(() => {
        if (lastPostedRef.current[channelId] === messageId) {
          delete lastPostedRef.current[channelId];
        }
      });
  }, [
    isLoggedIn,
    hasFocus,
    api,
    activeSpaceId,
    activeChannelId,
    activeChannelNewestMessageId,
    clearViewedChannelLocally,
    onChannelRead,
    onChannelReadPersisted,
  ]);

  return { resync };
}
